import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Building2, User, MapPin, Plus, Trash2, ArrowRight, Search, CheckCircle2, FileText, Briefcase, Star, CalendarCheck2 } from "lucide-react";
import { toast } from "sonner";
import BookQuoteAppointmentDialog from "@/components/BookQuoteAppointmentDialog";

const CLIENT_TYPES = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "government", label: "Government" },
  { value: "rural", label: "Rural" },
  { value: "other", label: "Other" },
];

const CONTACT_METHODS = [
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

function blankContact() {
  return {
    tmp_id: crypto.randomUUID(),
    first_name: "",
    last_name: "",
    email: "",
    mobile_phone: "",
    office_phone: "",
    role: "",
    is_primary: false,
    preferred_contact_method: "email",
  };
}

function blankAddress(label = "Head Office") {
  return {
    tmp_id: crypto.randomUUID(),
    label,
    line1: "",
    line2: "",
    suburb: "",
    state: "NSW",
    postcode: "",
    country: "Australia",
  };
}

export default function NewEnquiry() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState("choose"); // choose | search | create | done
  const [searchQ, setSearchQ] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);

  // form for new client
  const [name, setName] = useState("");
  const [abn, setAbn] = useState("");
  const [clientType, setClientType] = useState("residential");
  const [notes, setNotes] = useState("");
  const [contacts, setContacts] = useState([{ ...blankContact(), is_primary: true }]);
  const [addresses, setAddresses] = useState([blankAddress()]);

  const { data: results = [] } = useQuery({
    queryKey: ["client-search", searchQ],
    queryFn: async () => (await api.get(`/clients?search=${encodeURIComponent(searchQ)}`)).data,
    enabled: step === "search" && searchQ.length >= 2,
  });

  const canSubmitNew = useMemo(() => {
    // Only ONE identifier is required: a business name OR a contact first name.
    return !!(name.trim() || contacts[0]?.first_name?.trim());
  }, [name, contacts]);

  const createClient = useMutation({
    mutationFn: async () => {
      // Ensure exactly one primary
      const cs = contacts.map((c, i) => ({ ...c, is_primary: i === 0 ? true : c.is_primary }));
      const first = cs.findIndex((c) => c.is_primary);
      if (first !== 0) {
        cs.forEach((c, i) => { c.is_primary = i === first; });
      }
      const payload = {
        name: name.trim() || `${cs[0].first_name} ${cs[0].last_name}`.trim(),
        abn_acn: abn.trim() || null,
        client_type: clientType,
        notes: notes.trim() || null,
        addresses: addresses.filter((a) => a.line1?.trim()).map(({ tmp_id, ...a }) => a),
        contacts: cs.filter((c) => c.first_name?.trim()).map(({ tmp_id, ...c }) => ({
          ...c,
          email: c.email?.trim() || null,
          mobile_phone: c.mobile_phone?.trim() || null,
          office_phone: c.office_phone?.trim() || null,
        })),
      };
      const { data } = await api.post("/clients", payload);
      return data;
    },
    onSuccess: (data) => {
      toast.success(`${data.name} created${data.callback_booking_id ? " — call-back booked on the scheduler for tomorrow 8 AM" : ""}`);
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["bookings"] });
      // Jun 2026 (Trent): go straight to the new client's profile — skip the
      // booking/next-steps screen.
      navigate(`/clients/${data.id}`);
    },
    onError: (err) => {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(", ") : "Failed to create client");
    },
  });

  const updateContact = (i, patch) => {
    const next = [...contacts];
    next[i] = { ...next[i], ...patch };
    // Only one primary
    if (patch.is_primary) next.forEach((c, idx) => { if (idx !== i) c.is_primary = false; });
    setContacts(next);
  };
  const updateAddress = (i, patch) => {
    const next = [...addresses];
    next[i] = { ...next[i], ...patch };
    setAddresses(next);
  };

  const proceedToQuote = () => selectedClient && navigate(`/quotes/new?client_id=${selectedClient.id}`);
  const proceedToJob = () => selectedClient && navigate(`/jobs?client_id=${selectedClient.id}`);
  const viewClient = () => selectedClient && navigate(`/clients/${selectedClient.id}`);

  return (
    <div>
      <PageHeader
        title="New Enquiry"
        subtitle="Every job starts here. Select an existing client or create a new record."
        breadcrumb={["Clients", "New Enquiry"]}
      />

      <div className="px-6 lg:px-10 py-6 max-w-5xl mx-auto space-y-4">
        {/* Progress steps */}
        <div className="flex items-center gap-3 text-xs stamp-label text-slate-500 mb-6">
          <div className={step === "choose" ? "text-cyan-700 font-semibold" : ""}>1. Start</div>
          <span className="text-slate-300">→</span>
          <div className={step === "search" || step === "create" ? "text-cyan-700 font-semibold" : ""}>2. Client Details</div>
          <span className="text-slate-300">→</span>
          <div className={step === "done" ? "text-cyan-700 font-semibold" : ""}>3. Next Action</div>
        </div>

        {step === "choose" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card data-testid="enquiry-existing-btn" onClick={() => setStep("search")}
                  className="rounded-sm p-8 industrial-shadow cursor-pointer hover:border-cyan-400 transition-colors">
              <Search className="text-cyan-600 mb-3" size={28} />
              <div className="font-display font-bold text-xl text-slate-900 mb-1">Existing Client</div>
              <div className="text-sm text-slate-500">Search the client database and pick up where you left off.</div>
            </Card>
            <Card data-testid="enquiry-new-btn" onClick={() => setStep("create")}
                  className="rounded-sm p-8 industrial-shadow cursor-pointer hover:border-cyan-400 transition-colors">
              <Plus className="text-cyan-600 mb-3" size={28} />
              <div className="font-display font-bold text-xl text-slate-900 mb-1">New Client</div>
              <div className="text-sm text-slate-500">Create a fresh client record with contacts and addresses.</div>
            </Card>
          </div>
        )}

        {step === "search" && (
          <Card className="rounded-sm p-6 industrial-shadow space-y-4">
            <div>
              <Label className="stamp-label text-slate-600">Search existing clients</Label>
              <div className="relative mt-1.5">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <Input data-testid="enquiry-search-input" autoFocus value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                       placeholder="Business, contact name, email, phone…" className="rounded-sm h-11 pl-9" />
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto space-y-2" data-testid="enquiry-search-results">
              {searchQ.length < 2 && <div className="text-sm text-slate-400 text-center py-8">Type at least 2 characters to search.</div>}
              {results.map((c) => (
                <Card key={c.id} data-testid={`enquiry-result-${c.id}`}
                      onClick={() => { setSelectedClient(c); setStep("done"); }}
                      className="rounded-sm p-3 cursor-pointer hover:border-cyan-400 flex items-center gap-3">
                  <Building2 className="text-slate-400" size={20} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                    <div className="text-xs text-slate-500">
                      {c.primary_contact ? `${c.primary_contact.first_name} ${c.primary_contact.last_name}` : "No contact"}
                      {c.primary_contact?.mobile_phone && <> · {c.primary_contact.mobile_phone}</>}
                    </div>
                  </div>
                  <ArrowRight className="text-slate-300" size={16} />
                </Card>
              ))}
              {searchQ.length >= 2 && results.length === 0 && (
                <div className="text-sm text-slate-400 text-center py-6">
                  No matches. <button onClick={() => setStep("create")} className="text-cyan-600 hover:underline font-semibold">Create a new client instead</button>
                </div>
              )}
            </div>

            <div className="flex justify-between pt-3 border-t border-slate-100">
              <Button variant="outline" onClick={() => setStep("choose")} className="rounded-sm">Back</Button>
              <Button data-testid="enquiry-switch-to-new" variant="outline" onClick={() => setStep("create")} className="rounded-sm">
                <Plus size={14} className="mr-1.5" /> Create new instead
              </Button>
            </div>
          </Card>
        )}

        {step === "create" && (
          <div className="space-y-4">
            {/* Business */}
            <Card className="rounded-sm p-5 industrial-shadow">
              <div className="flex items-center gap-2 mb-4"><Building2 className="text-cyan-600" size={18} /><h3 className="font-display font-bold text-lg">Business Details</h3></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label>Business Name (optional)</Label>
                  <Input data-testid="client-name-input" value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm h-10 mt-1.5" placeholder="Leave blank for residential — we'll use the contact's name" />
                </div>
                <div>
                  <Label>ABN / ACN (optional)</Label>
                  <Input data-testid="client-abn-input" value={abn} onChange={(e) => setAbn(e.target.value)} className="rounded-sm h-10 mt-1.5 font-mono" placeholder="12 345 678 901" />
                </div>
                <div>
                  <Label>Client Type</Label>
                  <Select value={clientType} onValueChange={setClientType}>
                    <SelectTrigger data-testid="client-type-select" className="rounded-sm h-10 mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{CLIENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Internal Notes</Label>
                  <Textarea data-testid="client-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-sm mt-1.5" placeholder="Anything the team should know…" />
                </div>
              </div>
            </Card>

            {/* Contacts */}
            <Card className="rounded-sm p-5 industrial-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><User className="text-cyan-600" size={18} /><h3 className="font-display font-bold text-lg">Contacts</h3><Badge variant="outline" className="rounded-sm text-[10px]">{contacts.length}</Badge></div>
                <Button data-testid="add-contact-btn" size="sm" variant="outline" onClick={() => setContacts([...contacts, blankContact()])} className="rounded-sm h-8">
                  <Plus size={13} className="mr-1" /> Add contact
                </Button>
              </div>
              <div className="space-y-3">
                {contacts.map((c, i) => (
                  <Card key={c.tmp_id} className="rounded-sm p-3 bg-slate-50 border-slate-200" data-testid={`contact-block-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-600">Contact #{i + 1}</span>
                        {c.is_primary && <Badge variant="outline" className="rounded-sm text-[9px] border-cyan-300 bg-cyan-50 text-cyan-800"><Star size={9} className="mr-0.5" />Primary</Badge>}
                      </div>
                      <div className="flex gap-1.5">
                        {!c.is_primary && (
                          <Button data-testid={`contact-make-primary-${i}`} size="sm" variant="outline" onClick={() => updateContact(i, { is_primary: true })} className="rounded-sm h-7 text-[10px]">
                            Make primary
                          </Button>
                        )}
                        {contacts.length > 1 && (
                          <Button size="sm" variant="ghost" onClick={() => setContacts(contacts.filter((_, idx) => idx !== i))} className="rounded-sm h-7 w-7 p-0 text-rose-600 hover:bg-rose-50">
                            <Trash2 size={13} />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <Input placeholder="First name" data-testid={`contact-fn-${i}`} value={c.first_name} onChange={(e) => updateContact(i, { first_name: e.target.value })} className="rounded-sm h-9" />
                      <Input placeholder="Last name" data-testid={`contact-ln-${i}`} value={c.last_name} onChange={(e) => updateContact(i, { last_name: e.target.value })} className="rounded-sm h-9" />
                      <Input placeholder="Job title / role" value={c.role} onChange={(e) => updateContact(i, { role: e.target.value })} className="rounded-sm h-9" />
                      <Input placeholder="Email" type="email" value={c.email} onChange={(e) => updateContact(i, { email: e.target.value })} className="rounded-sm h-9" />
                      <Input placeholder="Mobile phone" value={c.mobile_phone} onChange={(e) => updateContact(i, { mobile_phone: e.target.value })} className="rounded-sm h-9 font-mono" />
                      <Input placeholder="Office phone (optional)" value={c.office_phone} onChange={(e) => updateContact(i, { office_phone: e.target.value })} className="rounded-sm h-9 font-mono" />
                      <div className="md:col-span-2">
                        <Select value={c.preferred_contact_method} onValueChange={(v) => updateContact(i, { preferred_contact_method: v })}>
                          <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Preferred contact method" /></SelectTrigger>
                          <SelectContent>{CONTACT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>Prefers {m.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>

            {/* Addresses */}
            <Card className="rounded-sm p-5 industrial-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><MapPin className="text-cyan-600" size={18} /><h3 className="font-display font-bold text-lg">Addresses</h3><Badge variant="outline" className="rounded-sm text-[10px]">{addresses.length}</Badge></div>
                <Button data-testid="add-address-btn" size="sm" variant="outline" onClick={() => setAddresses([...addresses, blankAddress(`Site ${addresses.length}`)])} className="rounded-sm h-8">
                  <Plus size={13} className="mr-1" /> Add address
                </Button>
              </div>
              <div className="space-y-3">
                {addresses.map((a, i) => (
                  <Card key={a.tmp_id} className="rounded-sm p-3 bg-slate-50 border-slate-200" data-testid={`address-block-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Input value={a.label} onChange={(e) => updateAddress(i, { label: e.target.value })} className="rounded-sm h-8 w-56 font-semibold text-xs" placeholder="Address label" />
                      {addresses.length > 1 && (
                        <Button size="sm" variant="ghost" onClick={() => setAddresses(addresses.filter((_, idx) => idx !== i))} className="rounded-sm h-7 w-7 p-0 text-rose-600 hover:bg-rose-50">
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-2 text-xs">
                      <AddressAutocomplete placeholder="Street address — start typing for suggestions" testid={`address-line1-${i}`} value={a.line1}
                        onChange={(v) => updateAddress(i, { line1: v })}
                        onPlace={(p) => updateAddress(i, { line1: p.line1 || a.line1, suburb: p.suburb, state: p.state || a.state, postcode: p.postcode })}
                        className="md:col-span-3" />
                      <Input placeholder="Unit / suite (optional)" value={a.line2} onChange={(e) => updateAddress(i, { line2: e.target.value })} className="rounded-sm h-9 md:col-span-3" />
                      <Input placeholder="Suburb" data-testid={`address-suburb-${i}`} value={a.suburb} onChange={(e) => updateAddress(i, { suburb: e.target.value })} className="rounded-sm h-9 md:col-span-3" />
                      <Input placeholder="State" value={a.state} onChange={(e) => updateAddress(i, { state: e.target.value })} className="rounded-sm h-9 md:col-span-1 font-mono uppercase" />
                      <Input placeholder="Postcode" data-testid={`address-postcode-${i}`} value={a.postcode} onChange={(e) => updateAddress(i, { postcode: e.target.value })} className="rounded-sm h-9 md:col-span-2 font-mono" />
                    </div>
                  </Card>
                ))}
              </div>
            </Card>

            <div className="flex justify-between pt-3">
              <Button variant="outline" onClick={() => setStep("choose")} className="rounded-sm h-11">Back</Button>
              <Button
                data-testid="create-client-submit"
                onClick={() => createClient.mutate()}
                disabled={!canSubmitNew || createClient.isPending}
                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm h-11 px-6"
              >
                {createClient.isPending ? "Creating…" : (<>Save & Continue <ArrowRight size={14} className="ml-1.5" /></>)}
              </Button>
            </div>
          </div>
        )}

        {step === "done" && selectedClient && (
          <Card className="rounded-sm p-6 sm:p-8 industrial-shadow text-center space-y-4">
            <CheckCircle2 className="text-emerald-500 mx-auto" size={40} />
            <div>
              <div className="font-display font-bold text-2xl text-slate-900">{selectedClient.name}</div>
              <div className="text-sm text-slate-500 mt-1">Client record ready. Book a quote visit or continue.</div>
            </div>

            {/* Primary CTA — Book quote appointment (new workflow chain) */}
            <div className="pt-3 max-w-md mx-auto">
              <Button
                data-testid="next-book-quote-btn"
                onClick={() => setBookOpen(true)}
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm h-14 flex-col gap-0.5"
              >
                <span className="flex items-center gap-2 font-display font-bold text-base"><CalendarCheck2 size={18} /> Book Quote Appointment</span>
                <span className="text-[10px] uppercase tracking-wider opacity-80">Adds to Calendar + Daily Tasks + Deals</span>
              </Button>
            </div>

            {/* Secondary options */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 max-w-3xl mx-auto">
              <Button data-testid="next-quote-btn" onClick={proceedToQuote} variant="outline" className="rounded-sm h-12 flex-col gap-0.5 text-xs">
                <FileText size={16} /> Skip — Go to Quote Builder
              </Button>
              <Button data-testid="next-job-btn" onClick={proceedToJob} variant="outline" className="rounded-sm h-12 flex-col gap-0.5 text-xs">
                <Briefcase size={16} /> Skip — Create Job
              </Button>
              <Button data-testid="next-view-btn" onClick={viewClient} variant="outline" className="rounded-sm h-12 flex-col gap-0.5 text-xs">
                <User size={16} /> View Client Record
              </Button>
            </div>
          </Card>
        )}

        <BookQuoteAppointmentDialog
          open={bookOpen}
          client={selectedClient}
          onClose={() => setBookOpen(false)}
          onScheduled={({ deal }) => {
            // Send the user to the Deals pipeline so they can see the enquiry
            navigate(`/deals?deal_id=${deal.id}`);
          }}
        />
      </div>
    </div>
  );
}
